/**
 * Terrain catalog (extensible by data, not code).
 *
 * Each entry maps an integer `id` (matches `TerrainHex.type` from the
 * engine) to its visual spec — color, PBR roughness, elevation render
 * mode, and an optional decoration kind. Adding a new terrain class
 * = append one entry here (+ optionally register a decoration in
 * `./decorations.ts` if it needs surface features).
 *
 * "Rules don't matter" per the PRD scope reset — this is purely
 * presentation. The engine owns transition timing + gameplay effects;
 * the renderer just paints what id it sees on each tile.
 *
 * Catalog is currently a TS literal; mirroring it to a future
 * `terrain/catalog.yaml` follows the same `cosmetics/catalog.yaml`
 * pattern when needed.
 */

import type { DecorationKind } from "./decorations";

export type ElevationRender =
  | "extruded" // hex prism extruded down to the floor; cliffs read at neighbors
  | "depressed" // tile sinks below the floor plane (water / pit)
  | "flat"; // hugs the floor plane; elevation field ignored

export interface TerrainSpec {
  /** Integer id — matches `TerrainHex.type` from the engine. */
  id: number;
  /** Display name (review panel). */
  label: string;
  /** Base diffuse color. */
  color: string;
  /** PBR roughness 0..1. */
  roughness: number;
  /** PBR metalness 0..1. */
  metalness: number;
  /** Top-surface color when this tile has taken damage (P5 destruction state). */
  damagedColor?: string;
  /** Floor color exposed when the tile has been knocked down to rubble. */
  rubbleColor?: string;
  /** How elevation renders. */
  elevationRender: ElevationRender;
  /** Vertical baseline offset added to the engine's `elevation` field, in tile-thickness units. */
  baseLift: number;
  /** Optional decoration painted on the tile's top face. */
  decoration?: DecorationKind;
  /** When true, this tile pulses an emissive shimmer (water, lava). */
  emissive?: boolean;
}

// B10 — IDs match `server/mecharena/state.py::TerrainType`:
//   0 CLEAR, 1 LIGHT_WOODS, 2 HEAVY_WOODS, 3 WATER, 4 ROUGH.
// Before B10 the catalog was IDs 0..4 but labelled Open/Ridge/Forest/
// Rubble/Water — a positional mismatch that drew water as rubble and
// rough as water once the engine started shipping terrain on the
// starter maps (server PR #496).
export const TERRAIN_CATALOG: ReadonlyArray<TerrainSpec> = [
  {
    // CLEAR — flat open ground.
    id: 0,
    label: "Clear",
    color: "#3b4252",
    roughness: 0.9,
    metalness: 0,
    elevationRender: "extruded",
    baseLift: 0,
  },
  {
    // LIGHT_WOODS — concealment cover (SQI penalty on shots through);
    // sparse sprigs, no elevation, distinct from heavy woods.
    id: 1,
    label: "Light Woods",
    color: "#5a7050",
    damagedColor: "#6a7855",
    rubbleColor: "#4a4032",
    roughness: 0.95,
    metalness: 0,
    elevationRender: "extruded",
    baseLift: 0,
    decoration: "forest_sprig",
  },
  {
    // HEAVY_WOODS — LOS-blocking. Darker, denser foliage to read as
    // "you can't shoot through this".
    id: 2,
    label: "Heavy Woods",
    color: "#2f4a35",
    damagedColor: "#4a5a40",
    rubbleColor: "#3a3022",
    roughness: 0.95,
    metalness: 0,
    elevationRender: "extruded",
    baseLift: 0,
    decoration: "forest_sprig",
  },
  {
    // WATER — sinks below floor, blue, emissive caustic.
    id: 3,
    label: "Water",
    color: "#1d4e6b",
    roughness: 0.2,
    metalness: 0.1,
    elevationRender: "depressed",
    baseLift: -0.5,
    decoration: "water_caustic",
    emissive: true,
  },
  {
    // ROUGH — broken ground, slow movement. Rocks + slight outcrop.
    id: 4,
    label: "Rough",
    color: "#5e6a82",
    roughness: 1,
    metalness: 0,
    elevationRender: "extruded",
    baseLift: 0,
    decoration: "rocky_outcrop",
  },
  // Add new terrain here. The renderer falls back to TERRAIN_FALLBACK
  // for any id not in the catalog so missing entries are visible but
  // don't crash.
];

export const TERRAIN_FALLBACK: TerrainSpec = {
  id: -1,
  label: "Unknown",
  color: "#2e3440",
  roughness: 0.9,
  metalness: 0,
  elevationRender: "extruded",
  baseLift: 0,
};

const BY_ID = new Map<number, TerrainSpec>(TERRAIN_CATALOG.map((s) => [s.id, s]));

export function specForTerrain(id: number): TerrainSpec {
  return BY_ID.get(id) ?? TERRAIN_FALLBACK;
}
